import { ArrowLeft, Sun, Moon } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import InfoSheet from "../../components/common/InfoSheet";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useState, useEffect } from "react";
import { message } from "antd";
import welcomeBg from "../../assets/homebg.png";
import PreferencesService from "../../services/PreferencesService";
import { useQueryClient } from "@tanstack/react-query";

const PreferencesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();

  // Unit preferences state
  const [weightUnit, setWeightUnit] = useState<string>("kg");
  const [heightUnit, setHeightUnit] = useState<string>("cm");
  const [language, setLanguage] = useState<string>("English");
  const [timeFormat, setTimeFormat] = useState<string>("12");
  const [distanceUnit, setDistanceUnit] = useState<string>("km");
  const [temperatureUnit, setTemperatureUnit] = useState<string>("c");
  const [loading, setLoading] = useState<boolean>(false);

  // Load preferences from API on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await PreferencesService.getUserPreferences();
        setWeightUnit(prefs.weight_unit);
        setHeightUnit(prefs.height_unit);

        setDistanceUnit(prefs.distance_unit);
        setTemperatureUnit(prefs.temperature_unit);

        // Still load language and time format from localStorage for now
        const savedLanguage =
          localStorage.getItem("glucera_language") || "English";
        const savedTimeFormat =
          localStorage.getItem("glucera_time_format") || "12";

        setLanguage(savedLanguage);
        setTimeFormat(savedTimeFormat);
      } catch (error) {
        // Fallback to localStorage
        const savedWeightUnit =
          localStorage.getItem("glucera_weight_unit") || "kg";
        const savedHeightUnit =
          localStorage.getItem("glucera_height_unit") || "cm";
        const savedDistanceUnit =
          localStorage.getItem("glucera_distance_unit") || "km";
        const savedTemperatureUnit =
          localStorage.getItem("glucera_temperature_unit") || "c";

        setWeightUnit(savedWeightUnit);
        setHeightUnit(savedHeightUnit);
        setDistanceUnit(savedDistanceUnit);
        setTemperatureUnit(savedTemperatureUnit);
      }
    };

    loadPreferences();
  }, []);

  // Save preferences to API when changed
  const handleWeightUnitChange = async (unit: string) => {
    setLoading(true);
    try {
      const currentPrefs = await PreferencesService.getUserPreferences();

      let nextHeightUnit = heightUnit;
      let nextDistanceUnit = distanceUnit;
      let nextTemperatureUnit = temperatureUnit;

      // Auto-select other units based on weight unit (User Requirement)
      if (unit === "kg") {
        nextHeightUnit = "cm";
        nextDistanceUnit = "km";
        nextTemperatureUnit = "c";
      } else if (unit === "lbs") {
        nextHeightUnit = "ft_in";
        nextDistanceUnit = "mi";
        nextTemperatureUnit = "f";
      }

      const updatedPrefs = {
        ...currentPrefs,
        weight_unit: unit as "kg" | "lbs",
        height_unit: nextHeightUnit as "cm" | "ft_in",
        distance_unit: nextDistanceUnit as "km" | "mi",
        temperature_unit: nextTemperatureUnit as "c" | "f",
      };

      const success =
        await PreferencesService.updateUserPreferences(updatedPrefs);
      if (success) {
        setWeightUnit(unit);
        setHeightUnit(nextHeightUnit);
        setDistanceUnit(nextDistanceUnit);
        setTemperatureUnit(nextTemperatureUnit);

        localStorage.setItem("glucera_weight_unit", unit);
        localStorage.setItem("glucera_height_unit", nextHeightUnit);
        localStorage.setItem("glucera_distance_unit", nextDistanceUnit);
        localStorage.setItem("glucera_temperature_unit", nextTemperatureUnit);

        // Invalidate home page data to refresh BMI with new unit
        queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      } else {
        message.error("Failed to update weight unit preference");
      }
    } catch (error) {
      message.error("Error updating preference");
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem("glucera_language", lang);
  };

  const handleTimeFormatChange = (format: string) => {
    setTimeFormat(format);
    localStorage.setItem("glucera_time_format", format);
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera/profilepage")}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">Settings</h1>
          <div className="w-10" />
        </div>

        {/* Theme Selection */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-title3 font-bold text-foreground">Appearance</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setTheme("light")}
              className={`rounded-xl border p-4 transition-all focus:outline-none ${theme === "light"
                ? "border-primary bg-primary/10 shadow-md"
                : "border-border hover:border-primary/50"
                }`}
            >
              <div className="flex flex-col items-center gap-2">
                <Sun className="h-7 w-7" />
                <span className="text-subhead font-bold text-foreground">
                  Light Mode
                </span>
              </div>
            </button>
            <button
              onClick={() => setTheme("dark")}
              className={`rounded-xl border p-4 transition-all focus:outline-none ${theme === "dark"
                ? "border-primary bg-primary/10 shadow-md"
                : "border-border hover:border-primary/50"
                }`}
            >
              <div className="flex flex-col items-center gap-2">
                <Moon className="h-7 w-7" />
                <span className="text-subhead font-bold text-foreground">
                  Dark Mode
                </span>
              </div>
            </button>
          </div>
        </Card>

        {/* Units */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="mb-4 flex items-center gap-2">
            <h3 className="text-title3 font-bold text-foreground">Units</h3>
            <InfoSheet title="About Units" ariaLabel="About units">
              <div className="space-y-2">
                <p>
                  Pick your <span className="font-semibold text-foreground">Weight Unit</span> and
                  the rest adjust automatically to match the same system:
                </p>
                <p>
                  <span className="font-semibold text-foreground">Metric (kg)</span> → cm, km, °C
                </p>
                <p>
                  <span className="font-semibold text-foreground">Imperial (lbs)</span> → ft-in, mi, °F
                </p>
                <p>
                  This keeps your measurements consistent across the app.
                </p>
              </div>
            </InfoSheet>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-subhead font-semibold text-foreground">
                Weight Unit
              </span>
              <select
                value={weightUnit}
                onChange={e => handleWeightUnitChange(e.target.value)}
                disabled={loading}
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-subhead font-semibold text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
              >
                <option value="kg">Kilograms (kg)</option>
                <option value="lbs">Pounds (lbs)</option>
              </select>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between py-2">
              <span className="text-subhead font-semibold text-foreground">
                Height Unit
              </span>
              <div className="rounded-lg border border-border bg-background px-3 py-1.5 text-subhead font-semibold text-foreground opacity-50">
                {heightUnit === "cm" ? "Centimeters (cm)" : "Ft-Inch"}
              </div>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between py-2">
              <span className="text-subhead font-semibold text-foreground">
                Distance Unit
              </span>
              <div className="rounded-lg border border-border bg-background px-3 py-1.5 text-subhead font-semibold text-foreground opacity-50">
                {distanceUnit === "km" ? "Kilometers (km)" : "Miles (mi)"}
              </div>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between py-2">
              <span className="text-subhead font-semibold text-foreground">
                Temperature Unit
              </span>
              <div className="rounded-lg border border-border bg-background px-3 py-1.5 text-subhead font-semibold text-foreground opacity-50">
                {temperatureUnit === "c" ? "Celsius (°C)" : "Fahrenheit (°F)"}
              </div>
            </div>
          </div>
        </Card>

        {/* Language */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-title3 font-bold text-foreground">Language</h3>
          <select
            value={language}
            onChange={e => handleLanguageChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-subhead font-semibold text-foreground focus:border-primary focus:outline-none"
          >
            <option value="English">English</option>
            <option value="Spanish">Spanish</option>
            <option value="French">French</option>
            <option value="German">German</option>
            <option value="Hindi">Hindi</option>
          </select>
        </Card>

        {/* Time Format */}
        {/* <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-title3 font-bold text-foreground">
            Time Format
          </h3>
          <select
            value={timeFormat}
            onChange={e => handleTimeFormatChange(e.target.value)}
            className="w-full rounded-lg border-2 border-border bg-background px-3 py-2 text-subhead font-semibold text-foreground focus:border-primary focus:outline-none"
          >
            <option value="12">12-hour (AM/PM)</option>
            <option value="24">24-hour</option>
          </select>
        </Card> */}
      </div>
    </div>
  );
};

export default PreferencesPage;
